import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings } from 'lucide-react';
import { Button } from './ui/Button';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  thirdParty: boolean;
}

const COOKIE_CONSENT_KEY = 'glyyd_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'glyyd_cookie_preferences';

export const CookieConsentBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true, cannot be disabled
    analytics: false, // Default OFF for COPPA compliance
    marketing: false, // Default OFF for COPPA compliance
    thirdParty: false, // Default OFF for COPPA compliance
  });

  useEffect(() => {
    // Check if user has already consented
    const hasConsented = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!hasConsented) {
      setShowBanner(true);
    } else {
      // Load saved preferences
      const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPreferences) {
        setPreferences(JSON.parse(savedPreferences));
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAcceptedPreferences: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      thirdParty: true,
    };
    savePreferences(allAcceptedPreferences);
  };

  const handleAcceptSelected = () => {
    savePreferences(preferences);
  };

  const handleRejectAll = () => {
    const minimalPreferences: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false,
      thirdParty: false,
    };
    savePreferences(minimalPreferences);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    localStorage.setItem('glyyd_consent_timestamp', new Date().toISOString());

    // Apply preferences (block/allow cookies based on consent)
    applyPreferences(prefs);

    setShowBanner(false);
    setShowPreferences(false);
  };

  const applyPreferences = (prefs: CookiePreferences) => {
    // This is where you would enable/disable various tracking scripts
    if (prefs.analytics) {
      // Enable analytics (Vercel Analytics, etc.)
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
        });
      }
    } else {
      // Disable analytics
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
        });
      }
    }

    // Handle other cookie types similarly
    if (!prefs.marketing) {
      // Disable marketing cookies
    }
    if (!prefs.thirdParty) {
      // Disable third-party cookies
    }
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Main Banner */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Cookie className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Cookie Consent</h3>
              </div>
              <p className="text-sm text-gray-600">
                We use cookies to enhance your experience and analyze our traffic. For users under
                18, we require parental consent for non-essential cookies. You can customize your
                preferences or accept all cookies.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowPreferences(true)}
                className="flex items-center gap-1"
              >
                <Settings className="h-4 w-4" />
                Preferences
              </Button>
              <Button variant="secondary" size="sm" onClick={handleRejectAll}>
                Reject All
              </Button>
              <Button variant="primary" size="sm" onClick={handleAcceptAll}>
                Accept All
              </Button>
            </div>
          </div>

          {/* COPPA Notice for Minors */}
          <div className="mt-3 p-3 bg-blue-50 rounded-md">
            <p className="text-xs text-blue-800">
              <strong>For Users Under 18:</strong> We prioritize your privacy. Non-essential cookies
              are disabled by default and require parental consent. Learn more in our{' '}
              <a href="/coppa-rights" className="underline">
                COPPA Rights
              </a>{' '}
              page.
            </p>
          </div>
        </div>
      </div>

      {/* Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Cookie Preferences</h2>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Essential Cookies */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 mb-1">Essential Cookies</h3>
                      <p className="text-sm text-gray-600">
                        Required for the website to function properly. These cannot be disabled.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      className="mt-1 h-4 w-4 text-blue-600 rounded cursor-not-allowed opacity-50"
                    />
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 mb-1">Analytics Cookies</h3>
                      <p className="text-sm text-gray-600">
                        Help us understand how visitors interact with our website to improve user
                        experience.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) =>
                        setPreferences({ ...preferences, analytics: e.target.checked })
                      }
                      className="mt-1 h-4 w-4 text-blue-600 rounded cursor-pointer"
                    />
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 mb-1">Marketing Cookies</h3>
                      <p className="text-sm text-gray-600">
                        Used to track visitors across websites to display relevant advertisements.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) =>
                        setPreferences({ ...preferences, marketing: e.target.checked })
                      }
                      className="mt-1 h-4 w-4 text-blue-600 rounded cursor-pointer"
                    />
                  </div>
                </div>

                {/* Third-Party Cookies */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 mb-1">Third-Party Cookies</h3>
                      <p className="text-sm text-gray-600">
                        Set by third-party services we use to enhance functionality and features.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.thirdParty}
                      onChange={(e) =>
                        setPreferences({ ...preferences, thirdParty: e.target.checked })
                      }
                      className="mt-1 h-4 w-4 text-blue-600 rounded cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button variant="secondary" onClick={() => setShowPreferences(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleAcceptSelected}>
                  Save Preferences
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Utility function to check if a specific cookie type is consented
export const hasCookieConsent = (cookieType: keyof CookiePreferences): boolean => {
  const preferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (!preferences) return false;

  const parsed = JSON.parse(preferences) as CookiePreferences;
  return parsed[cookieType] || false;
};

// Utility function to reset cookie consent (for testing or user request)
export const resetCookieConsent = () => {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  localStorage.removeItem(COOKIE_PREFERENCES_KEY);
  localStorage.removeItem('glyyd_consent_timestamp');
  window.location.reload();
};
